<template>
  <div :id="id" class="row align-items-center">
    <LmTextInput
      ref="inputFocus"
      :id="`label-${id}`"
      class="col-12 col-md-4"
      v-model="component.componentProps.label"
      @input="onLabelChange($event)"
      :label="$t('title')"
      :placeholder="$t('title')"
      :help="$t('comp_title_help')"
    />
    <LmTextInput
      v-if="validateSuperUser"
      :id="`name-${id}`"
      class="col-12 col-md-4"
      v-model="component.formObjectToSendToServerProperty"
      @input="$emit('input', component); $emit('validations-changes', {[component.formObjectToSendToServerProperty]:validations})"
      :label="$t('name')"
      :placeholder="$t('name')"
      :help="$t('comp_name_help')"
    />
    <LmTextInput
      class="col-12 col-md-4"
      :id="`placeholder-${id}`"
      v-model="component.componentProps.placeholder"
      @input="$emit('input', component)"
      :label="$t('placeholder')"
      :placeholder="$t('placeholder')"
      :help="$t('comp_placeholder_help')"
    />
    <LmSelect
      :id="`css-${id}`"
      class="col-12 col-md-4"
      v-model="component.class"
      :options="{
        content: [
          {value: 'col-3',text: '25%'},
          {value: 'col-6', text: '50%'},
          {value: 'col-9', text: '75%'},
          {value: 'col-12', text: '100%'}
        ]
      }"
      :label="$t('desktop_view_width')"
      :help="$t('help_width_form_element')"
      @input="$emit('input', component)"
    />
    <LmCheckbox
      :id="`disabled-${id}`"
      class="col-12 col-md-3"
      v-model="component.componentProps.disabled"
      :labelSingle="$t('disabled')"
      @input="$emit('input', component)"
    />
    <LmCheckbox
      :id="`predictive-${id}`"
      class="col-12 col-md-3"
      v-model="component.componentProps.async"
      labelSingle="Predictivo"
      @input="onAsyncChange"
    />
    <LmCheckbox
      :id="`multiple-${id}`"
      class="col-12 col-md-3"
      v-model="component.componentProps.multiple"
      :labelSingle="$t('multiple')"
      @input="$emit('input', component)"
    />
    <LmTextInput
      :id="`help-${id}`"
      class="col-12 col-md-8"
      v-model="component.componentProps.help"
      @input="$emit('input', component)"
      :label="$t('helper_text_setter')"
      :placeholder="$t('helper_text_setter')"
      :help="$t('helper_text_setter_help')"
    />
    <LmSelect
      class="col-12 col-md-4"
      :id="`childrenOf-${id}`"
      v-model="component.componentProps.childrenOf"
      :options="optionsChildOf"
      :label="$t('depends_of')"
      :help="$t('help_rely_on_form_element')"
      @input="onChildrenOfChange"
    />
    <hr class="w-100 mx-2">

    <div class="col-12">
      <h6 class="mb-4">Opciones dinámicas</h6>
      <p v-if="component.componentProps.options && component.componentProps.options.length">
        No puedes cargar opciones dinámicas si tienes opciones manuales cargadas.
      </p>
      <b-row class="align-items-center">
        <LmTreeSelect
          :disabled="!!component.componentProps.options && !!component.componentProps.options.length"
          class="col-9"
          label="Origen de datos"
          pathToGetOptions="/codificatorbuilder/"
          :multiple="component.componentProps.multipleOriginPaths"
          v-model="valueForOriginPaths"
          @input="onOriginPathsChange"
        />
        <LmCheckbox
          :disabled="(!!component.componentProps.options && !!component.componentProps.options.length) || !!component.componentProps.childrenOf"
          class="col-auto"
          labelSingle="Múltiples origenes"
          v-model="component.componentProps.multipleOriginPaths"
          @input="onMultipleOriginPathChange"
        />
      </b-row>
      <h6 class="mb-4">Opciones manuales</h6>
      <p v-if="!!(component.componentProps.pathToGetOptions && component.componentProps.pathToGetOptions.length) || !!component.componentProps.childrenOf">
        No puedes cargar opciones manuales si tienes origenes de datos o es un selector hijo.
      </p>
      <template v-if="component.componentProps.options">
        <div
          v-for="(option, index) in component.componentProps.options"
          :key="index"
          class="row align-items-center"
        >
          <LmTextInput
            :id="`text-${id}-${index}`"
            class="col-12 col-md-3"
            v-model="component.componentProps.options[index].id"
            :label="$t('id')"
            :placeholder="$t('id')"
          />
          <LmTextInput
            :id="`value-${id}-${index}`"
            class="col-12 col-md-6"
            v-model="component.componentProps.options[index].name"
            @input="$emit('input', component)"
            :label="$t('name')"
            :placeholder="$t('name')"
          />
          <font-awesome-icon
            :id="`trash-icon-${index}`"
            class="clickable mt-3 col-12 col-md-1"
            icon="trash"
            @click="removeOption(index)"
            v-b-tooltip.hover.top="$t('delete')"
          />
        </div>
      </template>
      <LmButton
        :disabled="!!(component.componentProps.pathToGetOptions && component.componentProps.pathToGetOptions.length) || component.componentProps.async"
        class="col-auto px-0"
        :id="`lm-button-${id}`"
        type="button"
        :value="$t('add_option')"
        buttonClass="btn-primary"
        @click="addOption"
      />
      <b-row class="mt-4 align-items-center">
        <LmTreeSelect
          :disabled="component.componentProps.allSelectedAsDefault || !!component.componentProps.childrenOf"
          class="col-md-9"
          label="Valores selecciondos por defecto"
          v-model="component.componentProps.defaultValue"
          @input="$emit('input', component)"
          :pathToGetOptions="component.componentProps.pathToGetOptions"
          :options="component.componentProps.options"
          :multiple="component.componentProps.multiple"
          :multipleOriginPaths="component.componentProps.multipleOriginPaths"
        />
        <LmCheckbox
          v-if="component.componentProps.multiple"
          class="col-auto"
          labelSingle="Todos seleccionados"
          v-model="component.componentProps.allSelectedAsDefault"
          @input="$emit('input', component)"
        />
      </b-row>
    </div>
    <hr class="w-100 mx-2">
    <div class="col-12">
      <h5>{{ $t('validations') }}</h5>
      <div class="row align-items-center">
        <LmCheckbox
          class="col-12 col-md-4"
          :labelSingle="$t('required')"
          v-model="validations.required"
          @input="$emit('validations-changes', {[component.formObjectToSendToServerProperty]:validations})"
        />
      </div>
    </div>
    <hr class="w-100 mx-2">
    <LmConditionalBuilderUi
      v-if="isVisibleCondition"
      class="col-12"
      :id="id"
      :leftValueToCompareOptions="leftValueToCompareOptions"
      :visibilityDependOnConditions="visibilityDependOnConditions"
      @changeVisibilityDependOnConditions="component.visibilityDependOnConditions = $event"
      @input="$emit('input', component)"
      />
    <LmConditionalByEditModeBuilderUi
      :component="component"
      @input="$emit('input', component)"
      />
  </div>
</template>

<script>
// Components
import LmButton from '@/components/form/elements/LmButton'
import LmCheckbox from '@/components/form/elements/LmCheckbox'
import LmSelect from '@/components/form/elements/LmSelect'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'

// Utils
import lmFormElementsBuilderUiMixin from '@/mixins/lmFormElementsBuilderUiMixin.js'

export default {
  mixins: [lmFormElementsBuilderUiMixin],
  data () {
    return {
      // dataOriginOptions: null,
      valueForOriginPaths: null
    }
  },
  components: {
    LmButton,
    LmCheckbox,
    LmSelect,
    LmTreeSelect
  },
  methods: {
    // Cambio entre múltiples o simple origen de datos
    onMultipleOriginPathChange () {
      this.component.componentProps.pathToGetOptions = null
      this.component.componentProps.originalPathToGetOptions = null
      this.valueForOriginPaths = null
    },
    // Cambio a busqueda predictiva
    onAsyncChange () {
      // Solo debo contemplar origenes de datos y no opciones manuales
      this.component.componentProps.options = null
      this.component.componentProps.pathToGetOptions = null
      this.component.componentProps.originalPathToGetOptions = null
      this.component.componentProps.pathToGetAsyncOptions = null
      this.valueForOriginPaths = null
      this.$emit('input', this.component)
    },
    // El componente se vuelve un hijo de otro
    onChildrenOfChange (event) {
      if (event === null) {

      } else {
        // Solo debo contemplar origenes de datos y no opciones manuales
        this.component.componentProps.options = null
        // Origen de datos debería ser simple
        this.component.componentProps.multipleOriginPaths = false
        this.component.componentProps.originalPathToGetOptions = null
        this.component.componentProps.pathToGetOptions = null
        // No debería tener valores por defecto
        this.component.componentProps.defaultValue = null
        this.component.componentProps.allSelectedAsDefault = false
      }

      this.$emit('input', this.component)
    },
    // Cambio los paths de origen
    onOriginPathsChange (event) {
      // Vacio los valores por defecto
      this.component.componentProps.defaultValue = null
      this.component.componentProps.pathToGetAsyncOptions = null
      this.component.componentProps.pathToGetOptions = null
      this.component.componentProps.originalPathToGetOptions = null

      if (this.component.componentProps.async) {
        // Si estoy en modo simple solo envio el path
        if (!this.component.componentProps.multipleOriginPaths) {
          const predictivePath = `/codificatorbuilder/predictive/codificators/${event?.id}`
          this.component.componentProps.originalPathToGetOptions = predictivePath || null
          this.component.componentProps.pathToGetAsyncOptions = predictivePath || null
          return this.$emit('input', this.component)
        }
        // En modo múltiple envio un array de objetos
        const predictivepaths = event.map(option => {
          return {
            id: option?.id,
            name: option?.name,
            path: `/codificatorbuilder/predictive/codificators/${option?.id}`
          }
        })
        this.component.componentProps.originalPathToGetOptions = predictivepaths
        this.component.componentProps.pathToGetAsyncOptions = predictivepaths
        return this.$emit('input', this.component)
      } else {
        // Si estoy en modo simple solo envio el path
        if (!this.component.componentProps.multipleOriginPaths) {
          this.component.componentProps.originalPathToGetOptions = event?.path || null
          this.component.componentProps.pathToGetOptions = event?.path || null
          return this.$emit('input', this.component)
        }
        // En modo múltiple envio un array de objetos
        this.component.componentProps.originalPathToGetOptions = event
        this.component.componentProps.pathToGetOptions = event
        return this.$emit('input', this.component)
      }
    },
    addOption () {
      this.component.componentProps.multipleOriginPaths = false
      // this.$emit('input', this.component)
      if (this.component.componentProps.options === null) {
        this.component.componentProps.options = []
      }
      this.component.componentProps.options.push({ id: '', name: '' })
    },
    removeOption (optionIndex) {
      this.component.componentProps.options.splice(optionIndex, 1)
      if (!this.component.componentProps.options.length) {
        // Vacio los valores por defecto
        this.component.componentProps.defaultValue = null
        // Envío null si no tengo opciones
        this.component.componentProps.options = null
      }
    }
  },
  props: {
    isVisibleCondition: {
      type: Boolean,
      default: true
    },
    optionsChildOf: {
      type: Object,
      default: () => ({ content: [] })
    }
  }
}
</script>
